// frontend/src/hooks/useUnsavedChangesWarning.ts
//
// BUG-2026-0189 — warn before losing unsaved test data.
//
// Every test workflow page tracks a `dirty` flag. When the tech has unsaved
// edits and tries to LEAVE the page by a browser-level action (refresh, close
// the tab, hit the browser Back button, or type a new URL), the browser fires
// `beforeunload`. Calling preventDefault + setting returnValue makes the
// browser show its native "Leave site? Changes you made may not be saved."
// prompt, so people no longer silently lose progress.
//
// This used to be copy-pasted (identically) into TestWorkflowPage, the
// Atterberg page and the PSD page, and was entirely MISSING from the seven
// delegated rock/triaxial workflow pages (UCSM, UCS, TCS, BTS, PLT, Cerchar,
// Triaxial). Extracting it to one hook removes the duplication and lets every
// workflow page opt in with a single line: `useUnsavedChangesWarning(dirty)`.
//
// NOTE: `beforeunload` covers browser-level navigation only. In-app SPA route
// changes (breadcrumb / top-nav clicks) are guarded separately by each page's
// leave dialog (handleCancel) — react-router v7's useBlocker needs a data
// router, which this app does not use.

import { useEffect } from 'react';

/**
 * Show the browser's native "unsaved changes" warning on refresh / close /
 * browser-back while `dirty` is true. No-op when `dirty` is false.
 */
export function useUnsavedChangesWarning(dirty: boolean): void {
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Legacy browsers require returnValue to be set for the prompt to show.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);
}
